import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  Platform,
  RefreshControl,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import DateTimePicker from '@react-native-community/datetimepicker';
import { supabase } from '../../api/supabase';

// Types
interface ReportData {
  totalIncome: number;
  totalExpenses: number;
  netProfit: number;
  cashInflow: number;
  cashOutflow: number;
  netCashFlow: number;
  pendingReceivables: number;
  pendingPayables: number;
}

interface WeeklySummary {
  week_start: string;
  week_end: string;
  total_income: number;
  total_expenses: number;
  net_profit: number;
  cash_inflow: number;
  cash_outflow: number;
  transaction_count: number;
}

type ReportType = 'profit_loss' | 'cash_flow' | 'balance_sheet' | 'weekly_summary';
type TimeRange = 'week' | 'month' | 'quarter' | 'year' | 'custom';

export default function FinancialReportsHub() {
  // State
  const [reportType, setReportType] = useState<ReportType>('profit_loss');
  const [timeRange, setTimeRange] = useState<TimeRange>('month');
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(new Date());
  const [showStartPicker, setShowStartPicker] = useState(false);
  const [showEndPicker, setShowEndPicker] = useState(false);

  // Data
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [weeklySummaries, setWeeklySummaries] = useState<WeeklySummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  function getDefaultStartDate(): Date {
    const date = new Date();
    date.setDate(1); // First day of current month
    return date;
  }

  useEffect(() => {
    loadReportData();
  }, [reportType, timeRange, startDate, endDate]);

  const loadReportData = async () => {
    try {
      setLoading(true);

      const start = startDate.toISOString().split('T')[0];
      const end = endDate.toISOString().split('T')[0];

      if (reportType === 'weekly_summary') {
        await loadWeeklySummaries(start, end);
      } else {
        await loadFinancialData(start, end);
      }
    } catch (error) {
      console.error('Error loading report:', error);
      Alert.alert('Error', 'Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const loadFinancialData = async (start: string, end: string) => {
    // Query company_cash_transactions for the date range
    const { data, error } = await supabase
      .from('company_cash_transactions')
      .select('*')
      .gte('date', start)
      .lte('date', end);

    if (error) throw error;

    const transactions = data || [];

    // Calculate metrics
    const income = transactions
      .filter((t) => t.amount > 0)
      .reduce((sum, t) => sum + t.amount, 0);

    const expenses = transactions
      .filter((t) => t.amount < 0)
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);

    const cashInflow = transactions
      .filter((t) => t.transaction_type === 'bank_deposit' || t.category === 'income')
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);

    const cashOutflow = transactions
      .filter((t) => t.transaction_type === 'expense' || t.category === 'expense')
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);

    // For balance sheet, query pending items
    let pendingReceivables = 0;
    let pendingPayables = 0;

    if (reportType === 'balance_sheet') {
      // Get pending driver payments
      const { data: pendingPayments } = await supabase
        .from('driver_daily_payments')
        .select('cash_amount, bank_amount')
        .gte('payment_date', start)
        .lte('payment_date', end);

      if (pendingPayments) {
        pendingReceivables = pendingPayments.reduce(
          (sum, p) => sum + (p.cash_amount || 0) + (p.bank_amount || 0),
          0
        );
      }

      // Get pending expenses
      const { data: pendingExpenses } = await supabase
        .from('company_expenses')
        .select('amount')
        .eq('status', 'pending')
        .gte('date', start)
        .lte('date', end);

      if (pendingExpenses) {
        pendingPayables = pendingExpenses.reduce((sum, e) => sum + e.amount, 0);
      }
    }

    setReportData({
      totalIncome: income,
      totalExpenses: expenses,
      netProfit: income - expenses,
      cashInflow,
      cashOutflow,
      netCashFlow: cashInflow - cashOutflow,
      pendingReceivables,
      pendingPayables,
    });
  };

  const loadWeeklySummaries = async (start: string, end: string) => {
    const { data, error } = await supabase
      .from('weekly_cash_flow_summary')
      .select('*')
      .gte('week_start', start)
      .lte('week_end', end)
      .order('week_start', { ascending: false });

    if (error) throw error;

    setWeeklySummaries(data || []);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadReportData();
    setRefreshing(false);
  };

  const handleTimeRangeChange = (range: TimeRange) => {
    setTimeRange(range);
    const now = new Date();
    let start = new Date();

    switch (range) {
      case 'week':
        start.setDate(now.getDate() - 7);
        break;
      case 'month':
        start.setMonth(now.getMonth() - 1);
        break;
      case 'quarter':
        start.setMonth(now.getMonth() - 3);
        break;
      case 'year':
        start.setFullYear(now.getFullYear() - 1);
        break;
      case 'custom':
        // Don't change dates for custom
        return;
    }

    setStartDate(start);
    setEndDate(now);
  };

  const handleExportPDF = () => {
    if (Platform.OS !== 'web') {
      Alert.alert('Export', 'PDF export is only available on web platform');
      return;
    }

    Alert.alert('Export PDF', 'PDF export functionality would be implemented here');
    // TODO: Implement PDF export using react-native-html-to-pdf or similar
  };

  const handleExportExcel = () => {
    if (Platform.OS !== 'web') {
      Alert.alert('Export', 'Excel export is only available on web platform');
      return;
    }

    Alert.alert('Export Excel', 'Excel export functionality would be implemented here');
    // TODO: Implement Excel export using xlsx library
  };

  const formatCurrency = (amount: number) => {
    return `AED ${Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const renderProfitLoss = () => {
    if (!reportData) return null;

    return (
      <View style={styles.reportContent}>
        <Text style={styles.reportTitle}>Profit & Loss Statement</Text>
        <Text style={styles.reportPeriod}>
          {formatDate(startDate.toISOString())} - {formatDate(endDate.toISOString())}
        </Text>

        {/* Income Section */}
        <View style={styles.reportSection}>
          <Text style={styles.sectionTitle}>INCOME</Text>
          <View style={styles.reportRow}>
            <Text style={styles.reportLabel}>Total Income</Text>
            <Text style={[styles.reportValue, styles.positiveValue]}>{formatCurrency(reportData.totalIncome)}</Text>
          </View>
        </View>

        {/* Expenses Section */}
        <View style={styles.reportSection}>
          <Text style={styles.sectionTitle}>EXPENSES</Text>
          <View style={styles.reportRow}>
            <Text style={styles.reportLabel}>Total Expenses</Text>
            <Text style={[styles.reportValue, styles.negativeValue]}>{formatCurrency(reportData.totalExpenses)}</Text>
          </View>
        </View>

        {/* Net Profit Section */}
        <View style={[styles.reportSection, styles.totalSection]}>
          <View style={styles.reportRow}>
            <Text style={styles.totalLabel}>NET PROFIT</Text>
            <Text
              style={[
                styles.totalValue,
                reportData.netProfit >= 0 ? styles.positiveValue : styles.negativeValue,
              ]}
            >
              {formatCurrency(reportData.netProfit)}
            </Text>
          </View>
          <Text style={styles.profitMargin}>
            Profit Margin: {reportData.totalIncome > 0 ? ((reportData.netProfit / reportData.totalIncome) * 100).toFixed(1) : 0}%
          </Text>
        </View>
      </View>
    );
  };

  const renderCashFlow = () => {
    if (!reportData) return null;

    return (
      <View style={styles.reportContent}>
        <Text style={styles.reportTitle}>Cash Flow Statement</Text>
        <Text style={styles.reportPeriod}>
          {formatDate(startDate.toISOString())} - {formatDate(endDate.toISOString())}
        </Text>

        {/* Cash Inflow */}
        <View style={styles.reportSection}>
          <Text style={styles.sectionTitle}>CASH INFLOW</Text>
          <View style={styles.reportRow}>
            <Text style={styles.reportLabel}>Total Inflow</Text>
            <Text style={[styles.reportValue, styles.positiveValue]}>{formatCurrency(reportData.cashInflow)}</Text>
          </View>
        </View>

        {/* Cash Outflow */}
        <View style={styles.reportSection}>
          <Text style={styles.sectionTitle}>CASH OUTFLOW</Text>
          <View style={styles.reportRow}>
            <Text style={styles.reportLabel}>Total Outflow</Text>
            <Text style={[styles.reportValue, styles.negativeValue]}>{formatCurrency(reportData.cashOutflow)}</Text>
          </View>
        </View>

        {/* Net Cash Flow */}
        <View style={[styles.reportSection, styles.totalSection]}>
          <View style={styles.reportRow}>
            <Text style={styles.totalLabel}>NET CASH FLOW</Text>
            <Text
              style={[
                styles.totalValue,
                reportData.netCashFlow >= 0 ? styles.positiveValue : styles.negativeValue,
              ]}
            >
              {formatCurrency(reportData.netCashFlow)}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const renderBalanceSheet = () => {
    if (!reportData) return null;

    const totalAssets = reportData.totalIncome + reportData.pendingReceivables;
    const totalLiabilities = reportData.totalExpenses + reportData.pendingPayables;
    const equity = totalAssets - totalLiabilities;

    return (
      <View style={styles.reportContent}>
        <Text style={styles.reportTitle}>Balance Sheet</Text>
        <Text style={styles.reportPeriod}>As of {formatDate(endDate.toISOString())}</Text>

        {/* Assets */}
        <View style={styles.reportSection}>
          <Text style={styles.sectionTitle}>ASSETS</Text>
          <View style={styles.reportRow}>
            <Text style={styles.reportLabel}>Cash & Bank</Text>
            <Text style={styles.reportValue}>{formatCurrency(reportData.totalIncome)}</Text>
          </View>
          <View style={styles.reportRow}>
            <Text style={styles.reportLabel}>Accounts Receivable</Text>
            <Text style={styles.reportValue}>{formatCurrency(reportData.pendingReceivables)}</Text>
          </View>
          <View style={[styles.reportRow, styles.subtotalRow]}>
            <Text style={styles.subtotalLabel}>Total Assets</Text>
            <Text style={styles.subtotalValue}>{formatCurrency(totalAssets)}</Text>
          </View>
        </View>

        {/* Liabilities */}
        <View style={styles.reportSection}>
          <Text style={styles.sectionTitle}>LIABILITIES</Text>
          <View style={styles.reportRow}>
            <Text style={styles.reportLabel}>Accounts Payable</Text>
            <Text style={styles.reportValue}>{formatCurrency(reportData.pendingPayables)}</Text>
          </View>
          <View style={[styles.reportRow, styles.subtotalRow]}>
            <Text style={styles.subtotalLabel}>Total Liabilities</Text>
            <Text style={styles.subtotalValue}>{formatCurrency(totalLiabilities)}</Text>
          </View>
        </View>

        {/* Equity */}
        <View style={[styles.reportSection, styles.totalSection]}>
          <View style={styles.reportRow}>
            <Text style={styles.totalLabel}>EQUITY</Text>
            <Text style={[styles.totalValue, equity >= 0 ? styles.positiveValue : styles.negativeValue]}>
              {formatCurrency(equity)}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const renderWeeklySummary = () => {
    return (
      <View style={styles.reportContent}>
        <Text style={styles.reportTitle}>Weekly Summaries</Text>
        <Text style={styles.reportPeriod}>
          {formatDate(startDate.toISOString())} - {formatDate(endDate.toISOString())}
        </Text>

        {weeklySummaries.length === 0 ? (
          <View style={styles.emptyState}>
            <Ionicons name="document-outline" size={48} color="#CCC" />
            <Text style={styles.emptyStateText}>No weekly summaries found</Text>
          </View>
        ) : (
          weeklySummaries.map((summary, index) => (
            <View key={index} style={styles.weeklyCard}>
              <Text style={styles.weekRange}>
                {formatDate(summary.week_start)} - {formatDate(summary.week_end)}
              </Text>

              <View style={styles.weeklyStats}>
                <View style={styles.weeklyStatItem}>
                  <Text style={styles.weeklyStatLabel}>Income</Text>
                  <Text style={[styles.weeklyStatValue, styles.positiveValue]}>
                    {formatCurrency(summary.total_income)}
                  </Text>
                </View>

                <View style={styles.weeklyStatItem}>
                  <Text style={styles.weeklyStatLabel}>Expenses</Text>
                  <Text style={[styles.weeklyStatValue, styles.negativeValue]}>
                    {formatCurrency(summary.total_expenses)}
                  </Text>
                </View>

                <View style={styles.weeklyStatItem}>
                  <Text style={styles.weeklyStatLabel}>Net Profit</Text>
                  <Text
                    style={[
                      styles.weeklyStatValue,
                      summary.net_profit >= 0 ? styles.positiveValue : styles.negativeValue,
                    ]}
                  >
                    {formatCurrency(summary.net_profit)}
                  </Text>
                </View>
              </View>

              <Text style={styles.transactionCount}>{summary.transaction_count} transactions</Text>
            </View>
          ))
        )}
      </View>
    );
  };

  return (
    <ScrollView style={styles.container} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>Financial Reports</Text>
        <Text style={styles.subtitle}>Generate professional financial statements</Text>
      </View>

      {/* Report Type Selector */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Report Type</Text>
        <View style={styles.reportTypeButtons}>
          <TouchableOpacity
            style={[styles.reportTypeButton, reportType === 'profit_loss' && styles.reportTypeButtonActive]}
            onPress={() => setReportType('profit_loss')}
          >
            <Ionicons
              name="trending-up"
              size={20}
              color={reportType === 'profit_loss' ? '#FFFFFF' : '#666'}
            />
            <Text
              style={[
                styles.reportTypeButtonText,
                reportType === 'profit_loss' && styles.reportTypeButtonTextActive,
              ]}
            >
              P&L
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.reportTypeButton, reportType === 'cash_flow' && styles.reportTypeButtonActive]}
            onPress={() => setReportType('cash_flow')}
          >
            <Ionicons name="cash" size={20} color={reportType === 'cash_flow' ? '#FFFFFF' : '#666'} />
            <Text
              style={[
                styles.reportTypeButtonText,
                reportType === 'cash_flow' && styles.reportTypeButtonTextActive,
              ]}
            >
              Cash Flow
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.reportTypeButton, reportType === 'balance_sheet' && styles.reportTypeButtonActive]}
            onPress={() => setReportType('balance_sheet')}
          >
            <Ionicons
              name="stats-chart"
              size={20}
              color={reportType === 'balance_sheet' ? '#FFFFFF' : '#666'}
            />
            <Text
              style={[
                styles.reportTypeButtonText,
                reportType === 'balance_sheet' && styles.reportTypeButtonTextActive,
              ]}
            >
              Balance Sheet
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.reportTypeButton, reportType === 'weekly_summary' && styles.reportTypeButtonActive]}
            onPress={() => setReportType('weekly_summary')}
          >
            <Ionicons
              name="calendar"
              size={20}
              color={reportType === 'weekly_summary' ? '#FFFFFF' : '#666'}
            />
            <Text
              style={[
                styles.reportTypeButtonText,
                reportType === 'weekly_summary' && styles.reportTypeButtonTextActive,
              ]}
            >
              Weekly
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Time Range Selector */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Time Period</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.timeRangeScroll}>
          {(['week', 'month', 'quarter', 'year', 'custom'] as TimeRange[]).map((range) => (
            <TouchableOpacity
              key={range}
              style={[styles.timeRangeButton, timeRange === range && styles.timeRangeButtonActive]}
              onPress={() => handleTimeRangeChange(range)}
            >
              <Text
                style={[
                  styles.timeRangeButtonText,
                  timeRange === range && styles.timeRangeButtonTextActive,
                ]}
              >
                {range.charAt(0).toUpperCase() + range.slice(1)}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        {/* Custom Date Range */}
        {timeRange === 'custom' && (
          <View style={styles.customDateRange}>
            <TouchableOpacity style={styles.dateButton} onPress={() => setShowStartPicker(true)}>
              <Ionicons name="calendar-outline" size={18} color="#666" />
              <Text style={styles.dateButtonText}>{formatDate(startDate.toISOString())}</Text>
            </TouchableOpacity>

            <Text style={styles.dateSeparator}>to</Text>

            <TouchableOpacity style={styles.dateButton} onPress={() => setShowEndPicker(true)}>
              <Ionicons name="calendar-outline" size={18} color="#666" />
              <Text style={styles.dateButtonText}>{formatDate(endDate.toISOString())}</Text>
            </TouchableOpacity>
          </View>
        )}

        {showStartPicker && (
          <DateTimePicker
            value={startDate}
            mode="date"
            display={Platform.OS === 'ios' ? 'spinner' : 'default'}
            onChange={(event, date) => {
              setShowStartPicker(Platform.OS === 'ios');
              if (date) setStartDate(date);
            }}
            maximumDate={endDate}
          />
        )}

        {showEndPicker && (
          <DateTimePicker
            value={endDate}
            mode="date"
            display={Platform.OS === 'ios' ? 'spinner' : 'default'}
            onChange={(event, date) => {
              setShowEndPicker(Platform.OS === 'ios');
              if (date) setEndDate(date);
            }}
            minimumDate={startDate}
            maximumDate={new Date()}
          />
        )}
      </View>

      {/* Export Buttons */}
      <View style={styles.exportContainer}>
        <TouchableOpacity style={styles.exportButton} onPress={handleExportPDF}>
          <Ionicons name="document-text-outline" size={20} color="#FFFFFF" />
          <Text style={styles.exportButtonText}>Export PDF</Text>
        </TouchableOpacity>

        <TouchableOpacity style={[styles.exportButton, styles.exportButtonExcel]} onPress={handleExportExcel}>
          <Ionicons name="grid-outline" size={20} color="#FFFFFF" />
          <Text style={styles.exportButtonText}>Export Excel</Text>
        </TouchableOpacity>
      </View>

      {/* Report Content */}
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#007AFF" />
          <Text style={styles.loadingText}>Generating report...</Text>
        </View>
      ) : (
        <>
          {reportType === 'profit_loss' && renderProfitLoss()}
          {reportType === 'cash_flow' && renderCashFlow()}
          {reportType === 'balance_sheet' && renderBalanceSheet()}
          {reportType === 'weekly_summary' && renderWeeklySummary()}
        </>
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F5F5F7',
  },
  header: {
    padding: 20,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#000000',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 15,
    color: '#666',
  },
  card: {
    backgroundColor: '#FFFFFF',
    margin: 15,
    marginTop: 0,
    padding: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#000',
    marginBottom: 15,
  },
  reportTypeButtons: {
    flexDirection: 'row',
    gap: 10,
  },
  reportTypeButton: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: '#F5F5F7',
    paddingVertical: 15,
    borderRadius: 10,
    gap: 6,
  },
  reportTypeButtonActive: {
    backgroundColor: '#007AFF',
  },
  reportTypeButtonText: {
    fontSize: 13,
    color: '#666',
  },
  reportTypeButtonTextActive: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  timeRangeScroll: {
    marginBottom: 10,
  },
  timeRangeButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 20,
    backgroundColor: '#F5F5F7',
    marginRight: 10,
  },
  timeRangeButtonActive: {
    backgroundColor: '#007AFF',
  },
  timeRangeButtonText: {
    fontSize: 14,
    color: '#666',
  },
  timeRangeButtonTextActive: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  customDateRange: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 15,
    gap: 10,
  },
  dateButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F5F5F7',
    paddingHorizontal: 15,
    paddingVertical: 12,
    borderRadius: 10,
    gap: 8,
  },
  dateButtonText: {
    fontSize: 14,
    color: '#000',
  },
  dateSeparator: {
    fontSize: 14,
    color: '#666',
  },
  exportContainer: {
    flexDirection: 'row',
    paddingHorizontal: 15,
    gap: 10,
    marginBottom: 15,
  },
  exportButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FF3B30',
    paddingVertical: 12,
    borderRadius: 10,
    gap: 8,
  },
  exportButtonExcel: {
    backgroundColor: '#34C759',
  },
  exportButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  loadingContainer: {
    alignItems: 'center',
    paddingVertical: 60,
  },
  loadingText: {
    fontSize: 16,
    color: '#666',
    marginTop: 15,
  },
  reportContent: {
    backgroundColor: '#FFFFFF',
    margin: 15,
    marginTop: 0,
    padding: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3,
  },
  reportTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#000',
    textAlign: 'center',
    marginBottom: 8,
  },
  reportPeriod: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginBottom: 25,
  },
  reportSection: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
    marginBottom: 12,
    letterSpacing: 0.5,
  },
  reportRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  reportLabel: {
    fontSize: 15,
    color: '#000',
  },
  reportValue: {
    fontSize: 15,
    fontWeight: '600',
    color: '#000',
  },
  subtotalRow: {
    borderTopWidth: 2,
    borderTopColor: '#E0E0E0',
    borderBottomWidth: 0,
    marginTop: 8,
  },
  subtotalLabel: {
    fontSize: 15,
    fontWeight: '600',
    color: '#000',
  },
  subtotalValue: {
    fontSize: 15,
    fontWeight: '600',
    color: '#000',
  },
  totalSection: {
    backgroundColor: '#F9F9F9',
    padding: 15,
    borderRadius: 10,
    marginTop: 10,
  },
  totalLabel: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#000',
  },
  totalValue: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  profitMargin: {
    fontSize: 13,
    color: '#666',
    textAlign: 'right',
    marginTop: 8,
  },
  positiveValue: {
    color: '#34C759',
  },
  negativeValue: {
    color: '#FF3B30',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyStateText: {
    fontSize: 16,
    color: '#999',
    marginTop: 10,
  },
  weeklyCard: {
    backgroundColor: '#F9F9F9',
    padding: 15,
    borderRadius: 10,
    marginBottom: 12,
  },
  weekRange: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000',
    marginBottom: 12,
  },
  weeklyStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  weeklyStatItem: {
    flex: 1,
    alignItems: 'center',
  },
  weeklyStatLabel: {
    fontSize: 12,
    color: '#666',
    marginBottom: 4,
  },
  weeklyStatValue: {
    fontSize: 16,
    fontWeight: '600',
  },
  transactionCount: {
    fontSize: 12,
    color: '#666',
    textAlign: 'center',
    marginTop: 8,
  },
});
